import { Box, DatePicker, Flex, Input, Toggle } from "@nulogy/components";
import LabelWithTooltip from "components/common/LabelWithTooltip";
import Codenames from "policies/Codenames";
import React from "react";
import styled from "styled-components";
import CustomerSelector from "./CustomerSelector";
import CustomWorkOrderFieldSelector from "./CustomWorkOrderFieldSelector";
import FilterLabel from "./FilterLabel";
import StatusSelector from "./StatusSelector";

const DateRangeSeparator = styled.div`
  padding: 7px;
  text-align: center;
`;

const showCompletedLabel = (
  <LabelWithTooltip
    label="Show fully scheduled, completed, cancelled"
    tooltip="Only underscheduled work orders are shown by default. Fully scheduled, completed, and cancelled work orders are hidden."
  />
);

const filterByScheduleViewLabel = (
  <LabelWithTooltip
    label="Filter by current schedule view"
    tooltip="Shows work orders that are within your current view of the schedule."
  />
);

const showMissingDataLabel = (
  <LabelWithTooltip
    label="Show work orders with warnings"
    tooltip="Work orders with invalid data for the production rate or work order quantity fields cannot be scheduled."
  />
);

export default function FilterSidebarView({
  currentFilter,
  onChangeToggleFilter,
  onChangeFilter,
  onChangeSelect,
  applicationContext,
  onTextSearch,
  onToggleFilterByScheduleView,
}) {
  const scheduledBetweenDisabled = currentFilter.filterByScheduleView;

  return (
    <Box marginBottom="20px">
      <Flex alignItems="center">
        <Toggle
          className="spec-toggle-scheduled"
          offText={showCompletedLabel}
          onClick={() => onChangeToggleFilter("showScheduledCompletedOrCancelled")}
          onText={showCompletedLabel}
          toggled={currentFilter.showScheduledCompletedOrCancelled}
        />
      </Flex>
      <Flex alignItems="center">
        <Toggle
          className="spec-toggle-missing-data"
          offText={showMissingDataLabel}
          onClick={() => onChangeToggleFilter("showMissingData")}
          onText={showMissingDataLabel}
          toggled={currentFilter.showMissingData}
        />
      </Flex>
      <Flex alignItems="center">
        <Toggle
          className="spec-toggle-filter-by-schedule-view"
          offText={filterByScheduleViewLabel}
          onClick={onToggleFilterByScheduleView}
          onText={filterByScheduleViewLabel}
          toggled={currentFilter.filterByScheduleView}
        />
      </Flex>
      <FilterLabel marginTop="12px" value="Scheduled" />
      <Flex className="customDatePicker" data-testid="scheduled-between-date-picker">
        <Box width={45 / 100}>
          <DatePicker
            className="spec-scheduled-between-starts-at"
            dateFormat="MMM d, yyyy"
            inputProps={{ disabled: scheduledBetweenDisabled, placeholder: scheduledBetweenDisabled ? " " : "From" }}
            onChange={(date) => {
              onChangeFilter({ scheduledBetween: { startsAt: date } });
            }}
            popperProps={{ strategy: "fixed" }}
            selected={currentFilter.scheduledBetween.startsAt}
          />
        </Box>
        <Box width={10 / 100}>
          <DateRangeSeparator>&mdash;</DateRangeSeparator>
        </Box>
        <Box width={45 / 100}>
          <DatePicker
            className="spec-scheduled-between-ends-at"
            dateFormat="MMM d, yyyy"
            disabled={currentFilter.filterByScheduleView}
            inputProps={{ disabled: scheduledBetweenDisabled, placeholder: scheduledBetweenDisabled ? " " : "To" }}
            onChange={(date) => {
              onChangeFilter({ scheduledBetween: { endsAt: date } });
            }}
            popperProps={{ strategy: "fixed" }}
            selected={currentFilter.scheduledBetween.endsAt}
          />
        </Box>
      </Flex>
      <FilterLabel value="Due date" />
      <Flex className="customDatePicker">
        <Box width={45 / 100}>
          <DatePicker
            className="spec-due-date-starts-at"
            dateFormat="MMM d, yyyy"
            inputProps={{ placeholder: "From" }}
            onChange={(date) => {
              onChangeFilter({ dueBetween: { startsAt: date } });
            }}
            popperProps={{ strategy: "fixed" }}
            selected={currentFilter.dueBetween.startsAt}
          />
        </Box>
        <Box width={10 / 100}>
          <DateRangeSeparator>&mdash;</DateRangeSeparator>
        </Box>
        <Box width={45 / 100}>
          <DatePicker
            className="spec-due-date-ends-at"
            dateFormat="MMM d, yyyy"
            inputProps={{ placeholder: "To" }}
            onChange={(date) => {
              onChangeFilter({ dueBetween: { endsAt: date } });
            }}
            popperProps={{ strategy: "fixed" }}
            selected={currentFilter.dueBetween.endsAt}
          />
        </Box>
      </Flex>
      <FilterLabel value="Material availability date" />
      <Flex className="customDatePicker">
        <Box width={45 / 100}>
          <DatePicker
            className="spec-material-availability-date-starts-at"
            dateFormat="MMM d, yyyy"
            inputProps={{ placeholder: "From" }}
            onChange={(date) => {
              onChangeFilter({ materialAvailabilityBetween: { startsAt: date } });
            }}
            popperProps={{ strategy: "fixed" }}
            selected={currentFilter.materialAvailabilityBetween.startsAt}
          />
        </Box>
        <Box width={10 / 100}>
          <DateRangeSeparator>&mdash;</DateRangeSeparator>
        </Box>
        <Box width={45 / 100}>
          <DatePicker
            className="spec-material-availability-date-ends-at"
            dateFormat="MMM d, yyyy"
            inputProps={{ placeholder: "To" }}
            onChange={(date) => {
              onChangeFilter({ materialAvailabilityBetween: { endsAt: date } });
            }}
            popperProps={{ strategy: "fixed" }}
            selected={currentFilter.materialAvailabilityBetween.endsAt}
          />
        </Box>
      </Flex>
      <FilterLabel value={`Planned start date (from ${Codenames.PackmanagerShort})`} />
      <Flex className="customDatePicker">
        <Box width={45 / 100}>
          <DatePicker
            className="spec-planned-start-date-starts-at"
            dateFormat="MMM d, yyyy"
            inputProps={{ placeholder: "From" }}
            onChange={(date) => {
              onChangeFilter({ plannedStartBetween: { startsAt: date } });
            }}
            popperProps={{ strategy: "fixed" }}
            selected={currentFilter.plannedStartBetween.startsAt}
          />
        </Box>
        <Box width={10 / 100}>
          <DateRangeSeparator>&mdash;</DateRangeSeparator>
        </Box>
        <Box width={45 / 100}>
          <DatePicker
            className="spec-planned-start-date-ends-at"
            dateFormat="MMM d, yyyy"
            inputProps={{ placeholder: "To" }}
            onChange={(date) => {
              onChangeFilter({ plannedStartBetween: { endsAt: date } });
            }}
            popperProps={{ strategy: "fixed" }}
            selected={currentFilter.plannedStartBetween.endsAt}
          />
        </Box>
      </Flex>
      <FilterLabel value="Work order code" />
      <Input
        className="searchFilterInput"
        defaultValue={currentFilter.workOrderCode}
        name="workOrderCode"
        onChange={(event) => {
          onTextSearch({ workOrderCode: event?.target?.value });
        }}
      />
      <FilterLabel value="Work order ID" />
      <Input
        className="searchFilterInput"
        defaultValue={currentFilter.externalId}
        name="externalId"
        onChange={(event) => {
          onTextSearch({ externalId: event?.target?.value });
        }}
      />
      <StatusSelector onChange={onChangeSelect("statuses")} selectedStatuses={currentFilter.statuses} />
      <CustomWorkOrderFieldSelector
        applicationContext={applicationContext}
        onChange={onChangeSelect("customWorkOrderFields")}
        selectedCustomWorkOrderFields={currentFilter.customWorkOrderFields}
      />
      <FilterLabel value="Item code" />
      <Input
        className="searchFilterInput"
        defaultValue={currentFilter.itemCode}
        name="itemCode"
        onChange={(event) => {
          onTextSearch({ itemCode: event?.target?.value });
        }}
      />
      <FilterLabel value="Item description" />
      <Input
        className="searchFilterInput"
        defaultValue={currentFilter.itemDescription}
        name="itemDescription"
        onChange={(event) => {
          onTextSearch({ itemDescription: event?.target?.value });
        }}
      />
      <CustomerSelector onChange={onChangeSelect("customers")} selectedCustomers={currentFilter.customers} />
    </Box>
  );
}
